import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import { useAuth } from "../context/AuthContext";
import {
  Card,
  List,
  Typography,
  Space,
  Button,
  Skeleton,
  Empty,
  Divider,
  Badge,
  Avatar,
  Modal,
  Form,
  Input,
  Alert,
  Breadcrumb,
  Row,
  Col,
  Popconfirm,
  notification,
  AutoComplete,
  Tag,
  Tooltip,
} from "antd";
import {
  InfoCircleOutlined,
  PlusOutlined,
  SyncOutlined,
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  WifiOutlined,
  CarOutlined,
  CoffeeOutlined,
  SafetyOutlined,
  StarOutlined,
  PhoneOutlined,
  FireOutlined,
  ThunderboltOutlined,
  CameraOutlined,
  LaptopOutlined,
  PrinterOutlined,
  CustomerServiceOutlined,
  MedicineBoxOutlined,
  HeartOutlined,
  GiftOutlined,
  LockOutlined,
  EnvironmentOutlined,
  HomeOutlined,
  BankOutlined,
  ShopOutlined,
  RestOutlined,
  GlobalOutlined,
} from "@ant-design/icons";
import "./AmenitiesListPage.css";

const { Title, Text } = Typography;

/**
 * 1) Normalize & match amenity name -> icon
 * - Handles: "WiFi", "WI-FI", "Wi Fi", "wireless internet"
 */
const normalizeAmenityName = (name) =>
  String(name || "")
    .toLowerCase()
    .trim()
    .replace(/[_/\\\-]+/g, " ")
    .replace(/[^\w\s]+/g, "") // remove punctuation
    .replace(/\s+/g, " ");

const AMENITY_ICON_RULES = [
  {
    key: "wifi",
    label: "Wi-Fi / Internet",
    icon: <WifiOutlined />,
    keywords: ["wifi", "wi fi", "wi-fi", "wireless", "internet", "broadband"],
  },
  {
    key: "parking",
    label: "Parking",
    icon: <CarOutlined />,
    keywords: ["parking", "car park", "carpark", "garage", "park"],
  },
  {
    key: "breakfast",
    label: "Breakfast",
    icon: <CoffeeOutlined />,
    keywords: ["breakfast", "coffee", "tea", "continental", "buffet breakfast"],
  },
  {
    key: "restaurant",
    label: "Restaurant",
    icon: <ShopOutlined />,
    keywords: ["restaurant", "dining", "meal", "kitchen", "cafe"],
  },
  {
    key: "pool",
    label: "Pool",
    icon: <SafetyOutlined />,
    keywords: ["pool", "swimming", "swim"],
  },
  {
    key: "gym",
    label: "Gym / Fitness",
    icon: <ThunderboltOutlined />,
    keywords: ["gym", "fitness", "workout", "training"],
  },
  {
    key: "spa",
    label: "Spa / Wellness",
    icon: <HeartOutlined />,
    keywords: ["spa", "wellness", "massage", "sauna", "steam"],
  },
  {
    key: "security",
    label: "Security",
    icon: <LockOutlined />,
    keywords: ["security", "cctv", "guard", "safe", "vault"],
  },
  {
    key: "laundry",
    label: "Laundry",
    icon: <RestOutlined />,
    keywords: ["laundry", "washing", "dry cleaning", "drycleaning", "iron", "ironing"],
  },
  {
    key: "roomservice",
    label: "Room Service",
    icon: <CustomerServiceOutlined />,
    keywords: ["room service", "roomservice", "in room dining", "inroom dining"],
  },
  {
    key: "reception",
    label: "Reception / Front Desk",
    icon: <PhoneOutlined />,
    keywords: ["reception", "front desk", "frontdesk", "concierge", "24 hour desk", "24h desk"],
  },
  {
    key: "aircon",
    label: "Air Conditioning",
    icon: <GlobalOutlined />,
    keywords: ["aircon", "air conditioning", "ac", "a c", "cooling"],
  },
  {
    key: "heating",
    label: "Heating",
    icon: <FireOutlined />,
    keywords: ["heating", "heater", "warm"],
  },
  {
    key: "workspace",
    label: "Workspace",
    icon: <LaptopOutlined />,
    keywords: ["workspace", "desk", "work desk", "office", "business"],
  },
  {
    key: "printing",
    label: "Printing",
    icon: <PrinterOutlined />,
    keywords: ["printer", "printing", "print", "photocopy", "copy"],
  },
  {
    key: "medical",
    label: "First Aid / Medical",
    icon: <MedicineBoxOutlined />,
    keywords: ["first aid", "medical", "clinic", "doctor", "nurse", "pharmacy"],
  },
  {
    key: "cameras",
    label: "CCTV / Cameras",
    icon: <CameraOutlined />,
    keywords: ["camera", "cctv", "surveillance"],
  },
  {
    key: "location",
    label: "Location / Nearby",
    icon: <EnvironmentOutlined />,
    keywords: ["location", "nearby", "city center", "town", "mall", "airport"],
  },
  {
    key: "family",
    label: "Family Friendly",
    icon: <HomeOutlined />,
    keywords: ["family", "kids", "children", "child", "baby"],
  },
  {
    key: "business",
    label: "Business / Conference",
    icon: <BankOutlined />,
    keywords: ["business", "conference", "meeting", "boardroom", "seminar"],
  },
  {
    key: "gift",
    label: "Special / Complimentary",
    icon: <GiftOutlined />,
    keywords: ["gift", "complimentary", "free", "welcome", "bonus"],
  },
];

// Returns best match rule for a name
const detectAmenityRule = (name) => {
  const n = normalizeAmenityName(name);

  if (!n) return null;

  // 1) Exact keyword contains match
  for (const rule of AMENITY_ICON_RULES) {
    for (const kw of rule.keywords) {
      const kwn = normalizeAmenityName(kw);
      if (!kwn) continue;

      // word boundary-ish match: contains the phrase
      if (n.includes(kwn)) return rule;
    }
  }

  // 2) Extra fallback heuristics (optional)
  // e.g., "wi fi" already handled, but you can add regex checks here if needed.

  return null;
};

const getAmenityIcon = (name) => {
  const rule = detectAmenityRule(name);
  return rule?.icon || <StarOutlined />;
};

const AmenitiesListPage = () => {
  const { authData } = useAuth();
  const navigate = useNavigate();

  const hotelId = authData?.hotelId;

  const [amenities, setAmenities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showInfoModal, setShowInfoModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAmenity, setEditingAmenity] = useState(null);

  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const addNameWatch = Form.useWatch("name", addForm);
  const editNameWatch = Form.useWatch("name", editForm);

  const breadcrumbItems = [
    { title: <Link to="/dashboard">Home</Link> },
    { title: "Amenities Management" },
  ];

  const amenityOptions = useMemo(() => {
    // AutoComplete options from known catalog (but users can still type custom)
    return AMENITY_ICON_RULES.map((r) => ({
      value: r.label,
      label: (
        <Space>
          <span style={{ color: "#1677ff" }}>{r.icon}</span>
          <span>{r.label}</span>
          <Tag style={{ marginLeft: 8 }} color="default">
            {r.key}
          </Tag>
        </Space>
      ),
    }));
  }, []);

  const fetchAmenities = useCallback(async () => {
    if (!hotelId) {
      setError("Hotel ID is not available.");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axiosInstance.get(`Amenities/Hotel/${hotelId}`);
      setAmenities(response.data || []);
    } catch (err) {
      console.error("Error fetching amenities:", err);
      setError("Failed to load amenities. Please try again later.");
    } finally {
      setLoading(false);
    }
  }, [hotelId]);

  useEffect(() => {
    fetchAmenities();
  }, [fetchAmenities]);

  const handleAddAmenity = async (values) => {
    try {
      const amenityPayload = {
        name: values.name,
        hotelId,
        // Optional (recommended if you add it on backend later):
        // iconKey: detectAmenityRule(values.name)?.key ?? null,
      };

      const response = await axiosInstance.post(`Amenities/Hotel/${hotelId}`, amenityPayload);
      setAmenities([...amenities, response.data]);

      setShowAddModal(false);
      addForm.resetFields();

      notification.success({
        message: "Success",
        description: "Amenity added successfully.",
      });
    } catch (err) {
      console.error("Error adding amenity:", err);
      notification.error({
        message: "Error",
        description: "Failed to add amenity. Please try again.",
      });
    }
  };

  const handleUpdateAmenity = async (values) => {
    try {
      const amenityPayload = {
        amenityId: editingAmenity.amenityId,
        name: values.name,
        // Optional (recommended if you add it on backend later):
        // iconKey: detectAmenityRule(values.name)?.key ?? null,
      };

      await axiosInstance.put(
        `Amenities/Hotel/${hotelId}/Amenity/${editingAmenity.amenityId}`,
        amenityPayload
      );

      setAmenities(
        amenities.map((a) =>
          a.amenityId === editingAmenity.amenityId ? { ...a, name: values.name } : a
        )
      );

      setEditingAmenity(null);

      notification.success({
        message: "Success",
        description: "Amenity updated successfully.",
      });
    } catch (err) {
      console.error("Error updating amenity:", err);
      notification.error({
        message: "Error",
        description: "Failed to update amenity. Please try again.",
      });
    }
  };

  const handleDeleteAmenity = async (id) => {
    try {
      await axiosInstance.delete(`Amenities/Hotel/${hotelId}/Amenity/${id}`);
      setAmenities(amenities.filter((a) => a.amenityId !== id));

      notification.success({
        message: "Success",
        description: "Amenity deleted successfully.",
      });
    } catch (err) {
      console.error("Error deleting amenity:", err);
      notification.error({
        message: "Error",
        description: "Failed to delete amenity. Please try again.",
      });
    }
  };

  const renderDetectedTag = (name) => {
    const rule = detectAmenityRule(name);
    if (!name) return <Tag>Type to detect icon</Tag>;
    if (!rule) return <Tag color="default">Unmapped (Default icon)</Tag>;
    return (
      <Tag color="blue">
        Detected: {rule.key}
      </Tag>
    );
  };

  return (
    <div className="amenities-page">
      <Breadcrumb items={breadcrumbItems} style={{ marginBottom: 16 }} />

      <Card
        variant="outlined"
        title={
          <Space>
            <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)} />
            <Text strong>Amenities Management</Text>
            <Badge count={amenities.length} showZero />
            <Button type="text" icon={<InfoCircleOutlined />} onClick={() => setShowInfoModal(true)} />
          </Space>
        }
        extra={
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
              Add Amenity
            </Button>
            <Button icon={<SyncOutlined />} onClick={fetchAmenities} loading={loading}>
              Refresh
            </Button>
          </Space>
        }
      >
        {error && (
          <Alert
            type="error"
            message={error}
            showIcon
            style={{ marginBottom: 16 }}
            action={
              <Button size="small" type="primary" onClick={fetchAmenities}>
                Retry
              </Button>
            }
          />
        )}

        {loading ? (
          <Skeleton active paragraph={{ rows: 6 }} />
        ) : amenities.length === 0 ? (
          <Empty
            description={
              <Space direction="vertical">
                <Text>No amenities found</Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddModal(true)}>
                  Add Amenity
                </Button>
              </Space>
            }
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <List
            itemLayout="horizontal"
            dataSource={amenities}
            renderItem={(amenity) => {
              const rule = detectAmenityRule(amenity.name);

              return (
                <List.Item
                  actions={[
                    <Button
                      key="edit"
                      icon={<EditOutlined />}
                      onClick={() => {
                        setEditingAmenity(amenity);
                        editForm.setFieldsValue({ name: amenity.name });
                      }}
                    />,
                    <Popconfirm
                      key="del"
                      title="Delete this amenity?"
                      onConfirm={() => handleDeleteAmenity(amenity.amenityId)}
                      okText="Yes"
                      cancelText="No"
                    >
                      <Button icon={<DeleteOutlined />} danger />
                    </Popconfirm>,
                  ]}
                >
                  <List.Item.Meta
                    avatar={
                      <Avatar
                        icon={getAmenityIcon(amenity.name)}
                        style={{ backgroundColor: "#f0f2f5", color: "#1677ff" }}
                      />
                    }
                    title={
                      <Space wrap>
                        <Text strong>{amenity.name}</Text>
                        {rule ? (
                          <Tooltip title={`Matched rule: ${rule.key}`}>
                            <Tag color="blue">{rule.key}</Tag>
                          </Tooltip>
                        ) : (
                          <Tag>default</Tag>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              );
            }}
          />
        )}
      </Card>

      {/* Add Amenity Modal */}
      <Modal
        title={
          <Space>
            <PlusOutlined />
            <Text strong>Add New Amenity</Text>
          </Space>
        }
        open={showAddModal}
        destroyOnHidden
        onCancel={() => {
          addForm.resetFields();
          setShowAddModal(false);
        }}
        footer={null}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddAmenity}>
          <Form.Item
            name="name"
            label="Amenity Name"
            rules={[
              { required: true, message: "Please enter amenity name" },
              { min: 2, message: "Minimum 2 characters" },
            ]}
          >
            <AutoComplete
              options={amenityOptions}
              placeholder="Type or pick an amenity (e.g., WI-FI)"
              filterOption={(inputValue, option) => {
                const iv = normalizeAmenityName(inputValue);
                const ov = normalizeAmenityName(option?.value);
                return ov.includes(iv);
              }}
            >
              <Input
                prefix={
                  <span style={{ color: "#1677ff" }}>
                    {getAmenityIcon(addNameWatch)}
                  </span>
                }
              />
            </AutoComplete>
          </Form.Item>

          <div style={{ marginTop: -6, marginBottom: 12 }}>
            {renderDetectedTag(addNameWatch)}
          </div>

          <Form.Item>
            <Space>
              <Button
                onClick={() => {
                  addForm.resetFields();
                  setShowAddModal(false);
                }}
              >
                Cancel
              </Button>
              <Button type="primary" htmlType="submit">
                Add Amenity
              </Button>
            </Space>
          </Form.Item>
        </Form>

        <Divider style={{ margin: "12px 0" }} />

        <Text type="secondary" style={{ fontSize: 12 }}>
          Icon selection is automatic based on keywords. Example: “WI-FI”, “Wi Fi”, “Wireless Internet” will map to the Wi-Fi icon.
        </Text>
      </Modal>

      {/* Edit Amenity Modal */}
      {editingAmenity && (
        <Modal
          title={
            <Space>
              <EditOutlined />
              <Text strong>Edit Amenity</Text>
            </Space>
          }
          open={!!editingAmenity}
          destroyOnHidden
          onCancel={() => setEditingAmenity(null)}
          footer={null}
        >
          <Form form={editForm} onFinish={handleUpdateAmenity} layout="vertical">
            <Form.Item
              name="name"
              label="Amenity Name"
              rules={[
                { required: true, message: "Please enter amenity name" },
                { min: 2, message: "Minimum 2 characters" },
              ]}
            >
              <AutoComplete
                options={amenityOptions}
                placeholder="Type or pick an amenity"
                filterOption={(inputValue, option) => {
                  const iv = normalizeAmenityName(inputValue);
                  const ov = normalizeAmenityName(option?.value);
                  return ov.includes(iv);
                }}
              >
                <Input
                  prefix={
                    <span style={{ color: "#1677ff" }}>
                      {getAmenityIcon(editNameWatch)}
                    </span>
                  }
                />
              </AutoComplete>
            </Form.Item>

            <div style={{ marginTop: -6, marginBottom: 12 }}>
              {renderDetectedTag(editNameWatch)}
            </div>

            <Form.Item>
              <Space>
                <Button onClick={() => setEditingAmenity(null)}>Cancel</Button>
                <Button type="primary" htmlType="submit">
                  Update
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>
      )}

      {/* Info Modal */}
      <Modal
        title={
          <Space>
            <InfoCircleOutlined />
            <Text strong>Amenities Management Guide</Text>
          </Space>
        }
        open={showInfoModal}
        destroyOnHidden
        onCancel={() => setShowInfoModal(false)}
        footer={[
          <Button key="close" type="primary" onClick={() => setShowInfoModal(false)}>
            Close
          </Button>,
        ]}
        width={600}
      >
        <div style={{ padding: "8px 0" }}>
          <Text>This module allows you to manage all amenities available at your hotel.</Text>

          <Divider orientation="left" style={{ margin: "16px 0" }}>
            Key Features
          </Divider>
          <ul style={{ paddingLeft: 20 }}>
            <li>Add new amenities to your hotel</li>
            <li>Edit existing amenity names</li>
            <li>Remove amenities no longer offered</li>
            <li>Icons are auto-detected from amenity names (keywords mapping)</li>
          </ul>

          <Divider orientation="left" style={{ margin: "16px 0" }}>
            Current Status
          </Divider>
          <Row gutter={16}>
            <Col span={12}>
              <Text strong>Total Amenities:</Text> {amenities.length}
            </Col>
            <Col span={12}>
              <Text strong>Last Updated:</Text> {new Date().toLocaleString()}
            </Col>
          </Row>
        </div>
      </Modal>
    </div>
  );
};

export default AmenitiesListPage;
